import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ReloadCreditTypesEnum, WithdrawCreditTypesEnum } from '../../enums/wallet.enum';
import { IEnum } from '../../interfaces/enum.interface';
import { ISelectChoices } from '../../interfaces/generic-select-config.interface';
import { IQuestionChoices } from '../../interfaces/radio-checkbox-config.interface';
import { EnumsService } from '../../services/enums.service';
import {
    GetClassTypesEnum,
    GetContactTypesEnum,
    GetCostsRepartitionEnum,
    GetCreditTypesEnum,
    GetCustodyTypesEnum,
    GetDebitTypesEnum,
    GetEducationLevelsEnum,
    GetEducationTypesEnum,
    GetGendersEnum,
    GetRateTypesEnum,
    GetRelativeTypesEnum,
    GetRolesEnum,
    GetStorageTypesEnum,
    SetInit,
} from '../actions/enums.action';
import { CreditTypeEnumV2 } from './../../enums/wallet.enum';
import { IClassType } from './../../interfaces/class-types.interface';

export interface EnumsStateModel {
    rolesEnum: IEnum[];
    gendersEnum: IEnum[];
    educationLevelsEnum: IEnum[];
    educationTypesEnum: IEnum[];
    costsRepartitionEnum: IEnum[];
    classTypes: IClassType[];
    rateTypes: IEnum[];
    custodyTypes: IEnum[];
    relativeTypes: IEnum[];
    contactTypes: IEnum[];
    creditTypes: IEnum[];
    debitTypes: IEnum[];
    storageEnum: IEnum[];
    init: boolean;
}

@State<EnumsStateModel>({
    name: 'enums',
    defaults: {
        rolesEnum: [],
        gendersEnum: [],
        educationLevelsEnum: [],
        educationTypesEnum: [],
        costsRepartitionEnum: [],
        classTypes: [],

        rateTypes: [],
        custodyTypes: [],
        relativeTypes: [],
        contactTypes: [],
        creditTypes: [],
        debitTypes: [],

        storageEnum: [],

        init: false,
    },
})
@Injectable()
export class EnumsState {
    constructor(private enumsService: EnumsService) {}

    @Selector()
    public static getRolesEnum(state: EnumsStateModel): IEnum[] {
        return state.rolesEnum;
    }

    @Selector()
    public static rolesChoices(state: EnumsStateModel): ISelectChoices[] {
        return state.rolesEnum.map((role: IEnum) => ({ value: role.type, label: 'ENUM.ROLES.' + role.type.toUpperCase() }));
    }

    @Selector()
    public static getGendersEnum(state: EnumsStateModel): IEnum[] {
        return state.gendersEnum;
    }

    @Selector()
    public static adultGenders(state: EnumsStateModel): IQuestionChoices[] {
        return state.gendersEnum.map((genderEnum: IEnum) => ({
            value: genderEnum.id,
            label: 'REPERTOIRE.FORMS.PERSONAL-DATA.GENDER.ADULT.' + genderEnum.type.toUpperCase(),
        }));
    }

    @Selector()
    public static studentGenders(state: EnumsStateModel): IQuestionChoices[] {
        return state.gendersEnum.map((genderEnum: IEnum) => ({
            value: genderEnum.id,
            label: 'REPERTOIRE.FORMS.PERSONAL-DATA.GENDER.STUDENT.' + genderEnum.type.toUpperCase(),
        }));
    }

    @Selector()
    public static getEducationLevelsEnum(state: EnumsStateModel): IEnum[] {
        return state.educationLevelsEnum;
    }

    @Selector()
    public static educationLevels(state: EnumsStateModel): IQuestionChoices[] {
        return state.educationLevelsEnum
            .map((edLevelEnum: IEnum) => ({
                value: edLevelEnum.id,
                label: 'ENUM.EDUCATION-LEVEL.' + edLevelEnum.type.toUpperCase(),
            }))
            .sort((a: IQuestionChoices, b: IQuestionChoices) => (a.value < b.value ? 1 : -1));
    }

    @Selector()
    public static getClassTypesEnum(state: EnumsStateModel): IClassType[] {
        return state.classTypes;
    }

    @Selector()
    public static classTypes(state: EnumsStateModel): IQuestionChoices[] {
        return state.classTypes.map((classType: IClassType) => ({
            value: classType.id,
            label: classType.name,
        }));
    }

    @Selector()
    public static getEducationTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.educationTypesEnum;
    }

    @Selector()
    public static educationTypes(state: EnumsStateModel): IQuestionChoices[] {
        return state.educationTypesEnum
            .map((edTypeEnum: IEnum) => ({
                value: edTypeEnum.id,
                label: 'ENUM.EDUCATION-TYPES.' + edTypeEnum.type.toUpperCase(),
            }))
            .sort((a: IQuestionChoices, b: IQuestionChoices) => (a.value < b.value ? -1 : 1));
    }

    @Selector()
    public static getCostsRepartitionEnum(state: EnumsStateModel): IEnum[] {
        return state.costsRepartitionEnum;
    }

    @Selector()
    public static costsRepartition(state: EnumsStateModel): IQuestionChoices[] {
        return state.costsRepartitionEnum.map((costRepartition: IEnum) => ({
            value: costRepartition.id,
            label: 'REPERTOIRE.FORMS.SETTINGS.STUDENT.RADIO-' + costRepartition.type.toUpperCase() + '-TITLE',
        }));
    }

    @Selector()
    public static rateTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.rateTypes;
    }

    @Selector()
    public static rateTypes(state: EnumsStateModel): ISelectChoices[] {
        return state.rateTypes.map((rateType: IEnum) => ({
            value: rateType.id,
            label: 'ENUM.RATE-TYPES.' + rateType.type.toUpperCase(),
        }));
    }

    @Selector()
    public static custodyTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.custodyTypes;
    }

    @Selector()
    public static relativeTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.relativeTypes;
    }

    @Selector()
    public static relativeTypes(state: EnumsStateModel): ISelectChoices[] {
        return state.relativeTypes.map((relativeType: IEnum) => ({
            value: relativeType.id,
            label: 'ENUM.RELATIVE-TYPES.' + relativeType.type.toUpperCase(),
        }));
    }

    @Selector()
    public static contactTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.contactTypes;
    }

    @Selector()
    public static contactTypes(state: EnumsStateModel): ISelectChoices[] {
        return state.contactTypes.map((contactType: IEnum) => ({
            value: contactType.id,
            label: 'ENUM.CONTACT-TYPES.' + contactType.type.toUpperCase(),
        }));
    }

    @Selector()
    public static creditTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.creditTypes;
    }

    @Selector()
    public static debitTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.debitTypes;
    }

    @Selector()
    public static reloadCreditTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.creditTypes.filter(
            (creditType: IEnum) =>
                creditType.type !== WithdrawCreditTypesEnum.TRANSFER && creditType.type !== WithdrawCreditTypesEnum.REFUND
        );
    }

    @Selector()
    public static withdrawCreditTypesEnum(state: EnumsStateModel): IEnum[] {
        return state.creditTypes.filter(
            (creditType: IEnum) => creditType.type !== ReloadCreditTypesEnum.STRIPE && creditType.type !== ReloadCreditTypesEnum.CASH
        );
    }

    @Selector()
    public static creditTypes(state: EnumsStateModel): ISelectChoices[] {
        return state.creditTypes.map((creditType: IEnum) => ({
            value: creditType.id,
            label: 'ENUM.CREDIT-TYPES.' + creditType.type.toUpperCase(),
        }));
    }

    @Selector()
    public static debitTypes(state: EnumsStateModel): ISelectChoices[] {
        return state.debitTypes.map((debitType: IEnum) => ({
            value: debitType.id,
            label: 'ENUM.DEBIT-TYPES.' + debitType.type.toUpperCase(),
        }));
    }

    @Selector()
    public static reloadCreditTypes(state: EnumsStateModel): ISelectChoices[] {
        const defaultOrder: CreditTypeEnumV2[] = [CreditTypeEnumV2.CASH, CreditTypeEnumV2.PAYMENT, CreditTypeEnumV2.REFOUND];
        return state.creditTypes
            .filter((creditType: IEnum) => creditType.type !== CreditTypeEnumV2.TRANSFER)
            .sort((a: IEnum, b: IEnum) => {
                const indexA = defaultOrder.indexOf(a.type as CreditTypeEnumV2);
                const indexB = defaultOrder.indexOf(b.type as CreditTypeEnumV2);
                if (indexA === null) return 1;
                if (indexB === null) return -1;
                return indexA - indexB;
            })
            .map((creditType: IEnum) => ({
                value: creditType.id,
                label: 'ENUM.CREDIT-TYPES.' + creditType.type.toUpperCase(),
            }));
    }

    @Selector()
    public static withdrawCreditTypes(state: EnumsStateModel): ISelectChoices[] {
        return state.creditTypes
            .filter(
                (creditType: IEnum) => creditType.type !== ReloadCreditTypesEnum.STRIPE && creditType.type !== ReloadCreditTypesEnum.CASH
            )
            .map((creditType: IEnum) => ({
                value: creditType.id,
                label: 'ENUM.CREDIT-TYPES.' + creditType.type.toUpperCase(),
            }));
    }

    @Selector()
    public static storageEnumChoices(state: EnumsStateModel): ISelectChoices[] {
        return state.storageEnum.map((storageEnum: IEnum) => ({
            value: storageEnum.id,
            label: storageEnum.name,
        }));
    }

    @Selector()
    public static getInit(state: EnumsStateModel): boolean {
        return state.init;
    }

    @Action(GetRolesEnum)
    public setRolesEnums(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.rolesEnum.length) {
            return this.enumsService.getRolesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            rolesEnum: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetGendersEnum)
    public setGendersEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.gendersEnum.length) {
            return this.enumsService.getGendersEnum().pipe(
                tap((result: IEnum[]) => {
                    ctx.patchState({
                        gendersEnum: result,
                    });
                })
            );
        }
    }

    @Action(GetEducationLevelsEnum)
    public setEducationLevelsEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.educationLevelsEnum.length) {
            return this.enumsService.getEducationLevelsEnum().pipe(
                tap((result: IEnum[]) => {
                    ctx.patchState({
                        educationLevelsEnum: result,
                    });
                })
            );
        }
    }

    @Action(GetEducationTypesEnum)
    public setEducationTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.educationTypesEnum.length) {
            return this.enumsService.getEducationTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            educationTypesEnum: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetCostsRepartitionEnum)
    public setCostsRepartitionEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.costsRepartitionEnum.length) {
            return this.enumsService.getCostsRepartitionEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            costsRepartitionEnum: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetClassTypesEnum)
    public setClassTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IClassType[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.classTypes.length) {
            return this.enumsService.getClassTypesEnum().pipe(
                tap((result: IClassType[]) => {
                    if (result) {
                        ctx.patchState({
                            classTypes: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetRateTypesEnum)
    public setRateTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.rateTypes.length) {
            return this.enumsService.getRateTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        const baseArray: IEnum[] = result;
                        const orderedArray: IEnum[] = baseArray.filter((value: IEnum) => value.id !== 2);
                        const valueToOrder: IEnum = baseArray.find((value: IEnum) => value.id === 2);
                        ctx.patchState({
                            rateTypes: [...orderedArray, valueToOrder],
                        });
                    }
                })
            );
        }
    }

    @Action(GetCustodyTypesEnum)
    public setCustodyTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.custodyTypes.length) {
            return this.enumsService.getCustodyTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            custodyTypes: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetRelativeTypesEnum)
    public setRelativeTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.relativeTypes.length) {
            return this.enumsService.getRelativeTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            relativeTypes: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetContactTypesEnum)
    public setContactTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.contactTypes.length) {
            return this.enumsService.getContactTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            contactTypes: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetCreditTypesEnum)
    public setCreditTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.creditTypes.length) {
            return this.enumsService.getCreditTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            creditTypes: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetStorageTypesEnum)
    public getStorageTypesEnum(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.storageEnum.length) {
            return this.enumsService.getStorageTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            storageEnum: result,
                        });
                    }
                })
            );
        }
    }

    @Action(GetDebitTypesEnum)
    public getDebitTypes(ctx: StateContext<EnumsStateModel>): Observable<IEnum[]> | void {
        const state: EnumsStateModel = ctx.getState();
        if (!state.debitTypes.length) {
            return this.enumsService.getDebitTypesEnum().pipe(
                tap((result: IEnum[]) => {
                    if (result) {
                        ctx.patchState({
                            debitTypes: result,
                        });
                    }
                })
            );
        }
    }

    @Action(SetInit)
    public setInit(ctx: StateContext<EnumsStateModel>): void {
        ctx.patchState({
            init: true,
        });
    }
}
